import './EditableNameDropdown.scss';
import React, { useState, useEffect } from 'react';
import { SizeProp } from '@fortawesome/fontawesome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition as IDSvg } from '@fortawesome/free-regular-svg-icons';
import {
    faXmark,
    faEdit,
    faSave,
    IconDefinition as IDSolid,
} from '@fortawesome/free-solid-svg-icons';

export interface EditableNameDropdownOption {
    label: string;
    value: string | number;
}

interface EditableNameDropdownProps {
    isEditMode: boolean;
    onEditModeChange?: (isEditing: boolean) => void;

    selectedValue?: string | number;
    options: EditableNameDropdownOption[];
    onItemClick?: (item?: EditableNameDropdownOption) => void;

    viewModeFormatter?: (
        selectedValue: EditableNameDropdownOption,
        editComponent: JSX.Element
    ) => JSX.Element;
    editModeFormatter?: (
        dropdownComponent: JSX.Element,
        cancelComponent: JSX.Element,
        saveComponent?: JSX.Element
    ) => JSX.Element;

    editIcon?: IDSolid | IDSvg;
    editIconSize?: SizeProp;
    cancelIcon?: IDSolid | IDSvg;
    cancelIconSize?: SizeProp;
    saveIcon?: IDSolid | IDSvg;
    saveIconSize?: SizeProp;

    onChange: (newValue: string | number) => boolean;
    onCancel?: () => void;
    onSave?: () => void;

    allowUnselect?: boolean;
    unselectedText?: string;
    saveOnChange?: boolean;
}

const EditableNameDropdown = (props: EditableNameDropdownProps) => {
    const [isEditMode, setIsEditMode] = useState(props.isEditMode);

    const [fieldValue, setFieldValue] = useState<
        EditableNameDropdownOption | undefined
    >(
        props.selectedValue
            ? props.options.find(
                  (option) => option.value === props.selectedValue
              )
            : undefined
    );

    useEffect(() => {
        setFieldValue(
            props.selectedValue
                ? props.options.find(
                      (option) => option.value === props.selectedValue
                  )
                : undefined
        );
    }, [props.selectedValue, props.options]);

    const editIconComponent = () => (
        <FontAwesomeIcon
            className="mb-2 mb-lg-0"
            icon={(props.editIcon ?? faEdit) as any}
            size={props.editIconSize ?? 'lg'}
            onClick={() => {
                setIsEditMode(true);
                props.onEditModeChange?.(true);
            }}
        />
    );

    const cancelIconComponent = () => (
        <FontAwesomeIcon
            className="mb-2 mb-lg-0"
            icon={(props.cancelIcon ?? faXmark) as any}
            size={props.cancelIconSize ?? 'lg'}
            onClick={() => {
                setFieldValue(
                    props.selectedValue
                        ? props.options.find(
                              (option) => option.value === props.selectedValue
                          )
                        : undefined
                );
                setIsEditMode(false);
                props.onEditModeChange?.(false);
            }}
        />
    );

    const saveIconComponent = () => (
        <FontAwesomeIcon
            className="mb-2 mb-lg-0"
            icon={(props.saveIcon ?? faSave) as any}
            size={props.saveIconSize ?? 'lg'}
            onClick={() => {
                if (fieldValue) {
                    props.onChange(fieldValue.value);
                }
                setIsEditMode(false);
                props.onEditModeChange?.(false);
            }}
        />
    );

    const dropdownComponent = () => (
        <select
            className="form-select mb-2 mb-lg-0"
            value={props.selectedValue ?? '-1'}
            onChange={(event) => {
                const option = props.options.find(
                    (opt) => opt.value == event.target.value
                );

                setFieldValue(option);
                const turnToViewIfEnabled = props.onChange(event.target.value);

                if (!!props.saveOnChange && turnToViewIfEnabled) {
                    setIsEditMode(false);
                    props.onEditModeChange?.(false);
                }
            }}>
            {props.allowUnselect && (
                <option value="-1">{props.unselectedText ?? 'None'}</option>
            )}
            {props.options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );

    const renderEditor = () => {
        if (props.editModeFormatter) {
            return (
                <div className="editable-name-dropdown-editor">
                    {props.editModeFormatter(
                        dropdownComponent(),
                        cancelIconComponent(),
                        !!props.saveOnChange ? undefined : saveIconComponent()
                    )}
                </div>
            );
        }

        return (
            <div className="editable-name-dropdown-editor">
                {dropdownComponent()}
                {cancelIconComponent()}
                {!!props.saveOnChange ? <></> : saveIconComponent()}
            </div>
        );
    };

    const itemLabelComponent = (itemLabelString: string) =>
        props.onItemClick ? (
            <div
                className={'editable-name-dropdown-item-view-link'}
                onClick={() => props.onItemClick(fieldValue)}>
                {itemLabelString}
            </div>
        ) : (
            <>{itemLabelString}</>
        );

    const renderContent = () => {
        if (props.viewModeFormatter) {
            return (
                <div className="editable-name-dropdown-view">
                    {props.viewModeFormatter(fieldValue, editIconComponent())}
                </div>
            );
        }

        const itemLabelString =
            fieldValue?.label ?? props.unselectedText ?? 'N/A';

        return (
            <div className="editable-name-dropdown-view">
                <>
                    {itemLabelComponent(itemLabelString)}
                    {editIconComponent()}
                </>
            </div>
        );
    };

    const render = () => {
        return isEditMode ? renderEditor() : renderContent();
    };

    return <div className="editable-name-dropdown-wrapper">{render()}</div>;
};

export default EditableNameDropdown;
